import { useState } from 'react'
import { Check, Lock, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import type {
  GoalJourney,
  JourneyStep,
  JourneyStepStatus,
} from '../../services/hierarchyGraphService'

interface JourneyRailProps {
  journey: GoalJourney
  goalTitle: string
  onNavigate: (entityType: string, entityId: number) => void
  /** Render the goal title above the rail (used in multi-goal contexts). */
  showGoalTitle?: boolean
}

/**
 * Horizontal journey timeline rendered above the graph canvas.
 *
 * Each step is a pill with one of three visual states:
 *  - `completed`: solid, check icon, clickable when it has an entity.
 *  - `unlocked`: solid with brand glow, clickable when it has an entity.
 *  - `locked`: dashed outline + lock icon; not clickable; description on hover.
 */
export function JourneyRail({
  journey,
  goalTitle,
  onNavigate,
  showGoalTitle = false,
}: JourneyRailProps) {
  const [collapsed, setCollapsed] = useState(false)
  const steps = journey.steps.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  if (steps.length === 0) return null

  const total = steps.length
  const completed = steps.filter((s) => s.status === 'completed').length
  const pctComplete = Math.round((completed / total) * 100)

  return (
    <div className="w-full">
      {showGoalTitle && (
        <button
          type="button"
          className="w-full flex items-center justify-between mb-2"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles
              className="w-3.5 h-3.5 shrink-0"
              style={{ color: 'var(--brand-primary)' }}
            />
            <span
              className="text-xs font-semibold truncate"
              style={{ color: 'var(--text-primary)' }}
            >
              {goalTitle}
            </span>
            <span
              className="text-app-nano uppercase tracking-wide shrink-0"
              style={{ color: 'var(--text-secondary)' }}
            >
              · journey {completed}/{total} · {pctComplete}%
            </span>
          </div>
          {collapsed ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronUp className="w-3.5 h-3.5" />
          )}
        </button>
      )}

      {!collapsed && (
        <div className="relative overflow-x-auto">
          <div
            className="absolute left-0 right-0 top-1/2 h-px"
            style={{
              background:
                'linear-gradient(to right, color-mix(in srgb, var(--brand-primary) 25%, transparent), color-mix(in srgb, var(--brand-primary) 12%, transparent))',
            }}
          />
          <div className="relative flex items-stretch gap-2 py-2 min-w-max">
            {steps.map((step, idx) => (
              <StepPill
                key={step.step_id ?? idx}
                step={step}
                isFirst={idx === 0}
                isLast={idx === steps.length - 1}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface StepPillProps {
  step: JourneyStep
  isFirst: boolean
  isLast: boolean
  onNavigate: (entityType: string, entityId: number) => void
}

function StepPill({ step, onNavigate }: StepPillProps) {
  const statusStyle = styleForStatus(step.status)
  const canNavigate =
    step.status !== 'locked' && !!step.entity_id && !!step.entity_type

  const handleClick = () => {
    if (!canNavigate || !step.entity_id || !step.entity_type) return
    onNavigate(step.entity_type, step.entity_id)
  }

  const tooltip = step.description
    ? `${step.title}\n${step.description}`
    : step.title

  const icon =
    step.status === 'completed' ? (
      <Check className="w-3 h-3 shrink-0" />
    ) : step.status === 'locked' ? (
      <Lock className="w-3 h-3 shrink-0" />
    ) : (
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{
          background: 'var(--brand-primary)',
          boxShadow: '0 0 0 3px color-mix(in srgb, var(--brand-primary) 30%, transparent)',
        }}
      />
    )

  return (
    <button
      type="button"
      disabled={!canNavigate}
      onClick={handleClick}
      title={tooltip}
      className="relative min-w-[140px] max-w-[200px] rounded-full px-3 py-1.5 flex items-center gap-2 text-xs transition-transform"
      style={{
        border: statusStyle.border,
        background: statusStyle.background,
        color: statusStyle.color,
        cursor: canNavigate ? 'pointer' : 'default',
        opacity: step.status === 'locked' ? 0.7 : 1,
      }}
    >
      {icon}
      <span className="flex-1 min-w-0 text-left">
        <span
          className="block truncate font-medium leading-tight"
          style={{ color: statusStyle.color }}
        >
          {step.title || `Step`}
        </span>
        <span
          className="block truncate text-app-nano uppercase tracking-wide"
          style={{ color: 'var(--text-secondary)', opacity: 0.85 }}
        >
          {step.child_type.replace('_', ' ')} · {step.status}
        </span>
      </span>
    </button>
  )
}

function styleForStatus(status: JourneyStepStatus): {
  border: string
  background: string
  color: string
} {
  switch (status) {
    case 'completed':
      return {
        border: '1.5px solid var(--semantic-success, #4F7A63)',
        background: 'color-mix(in srgb, var(--semantic-success) 14%, var(--bg-surface))',
        color: 'var(--text-primary)',
      }
    case 'unlocked':
      return {
        border: '1.5px solid var(--brand-primary, #7851A9)',
        background: 'color-mix(in srgb, var(--brand-primary) 12%, var(--bg-surface))',
        color: 'var(--text-primary)',
      }
    case 'locked':
    default:
      return {
        border: '1.5px dashed var(--border-strong, #CFC6DD)',
        background: 'var(--bg-surface, #FBFAFD)',
        color: 'var(--text-secondary)',
      }
  }
}
